// Emissão em sequência da Planilha Gran — a regra da fila, sem React, para rodar em
// `node --test` (views/Financeiro/PlanilhaGran/__tests__/filaEmissaoGran.test.ts).
//
// A fila não emite nada: ela só decide qual fatura abrir na janela de emissão de sempre
// (BoletoEmissionModal) e quando andar. Quem liga isso à tela é FinanceiroLayout.tsx.

export interface ItemFilaGran { faturaId: number; rotulo: string }

export interface FilaGran {
  itens: ItemFilaGran[];
  pos: number;
  emitidas: number[];
  puladas: number[];
  /** A janela da fatura da vez já abriu. */
  aberta: boolean;
  /** Parada esperando o operador: fechou a janela sem emitir, ou a fatura não abriu. */
  pausada: boolean;
  motivoPausa?: string;
}

/** Status em que o boleto já saiu — a fatura da vez andou. */
export const STATUS_JA_EMITIDA = ['Com boleto emitido / A enviar', 'Cobrança enviada'];

export function novaFila(itens: ItemFilaGran[]): FilaGran {
  return { itens, pos: 0, emitidas: [], puladas: [], aberta: false, pausada: false };
}

export const atualDaFila = (f: FilaGran): ItemFilaGran | undefined => f.itens[f.pos];
export const filaTerminou = (f: FilaGran) => f.pos >= f.itens.length;

const avancar = (f: FilaGran, como: 'emitidas' | 'puladas'): FilaGran => {
  const atual = atualDaFila(f);
  if (!atual) return f;
  return { ...f, [como]: [...f[como], atual.faturaId], pos: f.pos + 1, aberta: false, pausada: false, motivoPausa: undefined };
};

/**
 * Fatura da vez que a tela deve abrir agora, ou null. Não abre quando está pausada,
 * quando a janela dela já abriu, ou quando ela já é a fatura aberta no quadro.
 */
export function faturaParaAbrir(f: FilaGran, faturaAbertaId: string | number | null): number | null {
  if (f.pausada || f.aberta) return null;
  const atual = atualDaFila(f);
  if (!atual) return null;
  if (faturaAbertaId !== null && String(faturaAbertaId) === String(atual.faturaId)) return null;
  return atual.faturaId;
}

/**
 * O que fazer ANTES de abrir a fatura da vez, olhando o status dela no quadro:
 *  - já emitida (saiu enquanto a janela estava fechada) → conta como emitida e anda;
 *  - não está mais pronta (outra pessoa mexeu, ou sumiu do quadro) → pula;
 *  - pronta → abrir.
 */
export function antesDeAbrir(f: FilaGran, statusDaFatura: string | null | undefined): { fila: FilaGran; abrir: boolean; aviso?: string } {
  const atual = atualDaFila(f);
  if (!atual) return { fila: f, abrir: false };
  if (statusDaFatura && STATUS_JA_EMITIDA.includes(statusDaFatura)) return { fila: avancar(f, 'emitidas'), abrir: false };
  if (statusDaFatura !== 'Com boleto calculado') {
    return { fila: avancar(f, 'puladas'), abrir: false, aviso: `A fatura ${atual.faturaId} já não está pronta para emitir e foi pulada.` };
  }
  return { fila: f, abrir: true };
}

/** A fatura da vez não abriu (trava de outro usuário, erro de rede): pausa com o motivo. */
export const naoAbriu = (f: FilaGran): FilaGran =>
  ({ ...f, pausada: true, motivoPausa: 'Esta fatura não abriu (pode estar aberta com outra pessoa).' });

/**
 * Observa a fatura aberta no quadro (id e status) e devolve a fila atualizada:
 *  - a da vez abriu → marca aberta;
 *  - a da vez passou a emitida → conta e anda;
 *  - a janela da vez fechou sem emitir → pausa.
 * Devolve a MESMA referência quando nada muda (a tela usa isso para não re-renderizar).
 */
export function observar(f: FilaGran, faturaAbertaId: string | number | null, statusAberta: string | null | undefined): FilaGran {
  if (f.pausada) return f;
  const atual = atualDaFila(f);
  if (!atual) return f;
  const abertaAgora = faturaAbertaId !== null && faturaAbertaId !== undefined && String(faturaAbertaId) === String(atual.faturaId);
  if (abertaAgora) {
    if (statusAberta && STATUS_JA_EMITIDA.includes(statusAberta)) return avancar(f, 'emitidas');
    return f.aberta ? f : { ...f, aberta: true };
  }
  return f.aberta ? { ...f, aberta: false, pausada: true, motivoPausa: undefined } : f;
}

export const pular = (f: FilaGran): FilaGran => avancar(f, 'puladas');

export const continuar = (f: FilaGran): FilaGran => ({ ...f, pausada: false, aberta: false, motivoPausa: undefined });
